/**
 * iter_226 · Phase 22 — Singles Net Earnings panel.
 *
 * Pulls from GET /api/singles/mine/dashboard (auth) and renders:
 *   • gross_usd, platform_fee_usd, processing_fee_usd, artist_net_usd
 *   • pending_usd + 7-day availability date (settlement_hold_days)
 *   • available_usd, refunds_usd, recoverable_negative_usd
 *   • sales count / active listings / paused listings
 *
 * Zero side-effects — never mints coins, never mutates listings.
 * Self-hides when the backend returns 401/503 (not eligible / not auth'd).
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

const Stat = ({ label, value, sub, accent = "emerald", testId }) => {
  const accents = {
    emerald: "from-emerald-500/15 to-teal-500/10 border-emerald-500/30 text-emerald-100",
    amber:   "from-amber-500/15 to-yellow-500/10 border-amber-500/40 text-amber-100",
    rose:    "from-rose-500/15 to-pink-500/10 border-rose-500/30 text-rose-100",
    cyan:    "from-cyan-500/15 to-blue-500/10 border-cyan-500/30 text-cyan-100",
    purple:  "from-purple-500/15 to-fuchsia-500/10 border-purple-500/30 text-purple-100",
  };
  return (
    <Card
      className={`p-3 sm:p-4 bg-gradient-to-br ${accents[accent] || accents.emerald} border`}
      data-testid={testId}
    >
      <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] font-black text-white/70">{label}</p>
      <p className="text-xl sm:text-2xl font-black text-white mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-[10px] text-white/60 mt-1">{sub}</p>}
    </Card>
  );
};

const SinglesEarningsPanel = () => {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | hidden

  useEffect(() => {
    let alive = true;
    (async () => {
      // First check access — hide panel entirely for regular artists
      // while singles.enabled=false.
      try {
        const acc = await api.get("/singles/access/status");
        if (!alive) return;
        if (!acc.data?.can_view) { setState("hidden"); return; }
      } catch { if (alive) { setState("hidden"); } return; }
      try {
        const r = await api.get("/singles/mine/dashboard");
        if (!alive) return;
        setData(r.data);
        setState("ok");
      } catch (e) {
        if (!alive) return;
        // 401 or 503 → silently hide (user simply isn't signed in / gate closed).
        setState("hidden");
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state !== "ok" || !data) return null;

  const t = data.totals || {};
  const b = data.balances || {};
  const c = data.counts || {};
  const holdDays = data.settlement_hold_days || 7;

  return (
    <div className="mb-6" data-testid="singles-earnings-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-black text-white flex items-center gap-2">
            💿 Singles Net Earnings
          </h2>
          <p className="text-[11px] text-emerald-200/70">
            Direct-to-consumer single sales · {holdDays}-day settlement hold before earnings become available.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/library/singles" className="text-[11px] text-emerald-300 hover:text-emerald-200 underline" data-testid="singles-earnings-library-link">
            My Singles Library →
          </Link>
          <Link to="/beats?tab=singles" className="text-[11px] text-cyan-300 hover:text-cyan-200 underline" data-testid="singles-earnings-store-link">
            Singles Store →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-3">
        <Stat
          label="Gross single sales"
          value={fmtUsd(t.gross_usd)}
          sub={`${c.sales || 0} sales`}
          accent="emerald"
          testId="singles-gross"
        />
        <Stat
          label="RGM platform fee"
          value={`− ${fmtUsd(t.platform_fee_usd)}`}
          sub="Configured per tier"
          accent="amber"
          testId="singles-platform-fee"
        />
        <Stat
          label="Actual processing fee"
          value={`− ${fmtUsd(t.processing_fee_usd)}`}
          sub="From Stripe balance txn"
          accent="cyan"
          testId="singles-processing-fee"
        />
        <Stat
          label="Artist net (settled)"
          value={fmtUsd(t.artist_net_usd)}
          sub="Post-fee earnings"
          accent="purple"
          testId="singles-artist-net"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-3">
        <Stat
          label={`Pending (${holdDays}-day hold)`}
          value={fmtUsd(b.pending_usd)}
          sub="Held for chargeback protection"
          accent="amber"
          testId="singles-pending"
        />
        <Stat
          label="Available"
          value={fmtUsd(b.available_usd)}
          sub="Ready for payout"
          accent="emerald"
          testId="singles-available"
        />
        <Stat
          label="Refunds / disputes"
          value={fmtUsd(b.refunded_usd)}
          sub="Reversed sales"
          accent="rose"
          testId="singles-refunds"
        />
        <Stat
          label="Recoverable negative"
          value={fmtUsd(b.recoverable_negative_usd)}
          sub="Owed back to RGM"
          accent="rose"
          testId="singles-recoverable-negative"
        />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Stat label="Active listings" value={c.active || 0} accent="cyan" testId="singles-listings-active" />
        <Stat label="Drafts / pending" value={c.drafts || 0} accent="purple" testId="singles-listings-drafts" />
        <Stat label="Paused" value={c.paused || 0} accent="amber" testId="singles-listings-paused" />
      </div>

      <p className="text-[10px] text-white/40 mt-2 italic">
        Pending sales become available exactly {holdDays} days after the sale settles.
        Refunds are reversed atomically and produce an audit-logged ledger entry.
      </p>
    </div>
  );
};

export default SinglesEarningsPanel;
